import { ISize } from "../core/types/math_types.js";
import { WebGLTextureSource } from "../core/types/webgl_types.js";

/**
 * Universal utilities for handling WebGL texture sources.
 * Helps with common tasks like getting dimensions and preparing a canvas
 * for GPU upload.
 */
export class WebGLTextureUtils
{
  private static temp_canvas : HTMLCanvasElement | null = null;
  private static temp_ctx    : CanvasRenderingContext2D | null = null;

  /**
   * Safe way to get width and height from any WebGLTextureSource without 'any'.
   */
  public static getSourceSize( source: WebGLTextureSource ): ISize
  {
    if ( source instanceof HTMLImageElement )
      return { w: source.naturalWidth || source.width, h: source.naturalHeight || source.height };
    
    return { w: source.width, h: source.height };
  }

  /**
   * Prepares a temporary internal canvas by drawing the source into it.
   * This is useful for "cloning" or processing a texture before WebGL upload.
   * 
   * WARNING: Uses a shared static canvas. The result must be used immediately 
   * (e.g. passed to allocateInAtlas) before any other call to prepareCanvas.
   */
  public static prepareCanvas( source: WebGLTextureSource, pad: number = 2 ): HTMLCanvasElement
  {
    const { w, h } = this.getSourceSize( source );
    const padded_w = w + pad * 2;
    const padded_h = h + pad * 2;
    
    if ( !this.temp_canvas )
    {
      this.temp_canvas = document.createElement( "canvas" );
      this.temp_ctx    = this.temp_canvas.getContext( "2d", { willReadFrequently: true } );
    }

    if ( this.temp_canvas.width !== padded_w || this.temp_canvas.height !== padded_h )
    {
      this.temp_canvas.width  = padded_w;
      this.temp_canvas.height = padded_h;
    }

    this.temp_ctx!.clearRect( 0, 0, padded_w, padded_h );
    this.temp_ctx!.drawImage( source as CanvasImageSource, pad, pad, w, h );

    return this.temp_canvas;
  }
}
